import type { ElementType, ReactNode } from "react";

const PADDING = {
  sm: "p-3",
  md: "p-4",
  lg: "p-6",
} as const;

const TONE = {
  default: "border-ink-700 bg-ink-900/40",
  success: "border-signal-500/40 bg-signal-500/10",
  danger: "border-red-900/50 bg-red-950/30",
} as const;

type Padding = keyof typeof PADDING;
type Tone = keyof typeof TONE;

interface CardProps {
  as?: ElementType;
  padding?: Padding;
  tone?: Tone;
  /** Adds the hover-border treatment used for clickable panels (e.g. the Home nav cards). */
  interactive?: boolean;
  className?: string;
  children?: ReactNode;
  [key: string]: unknown;
}

/**
 * The bordered panel treatment used everywhere — centralized so every card
 * gets the same raised fill, radius and shadow instead of sitting flush
 * with the page background. `as` lets a Link or list item render with the
 * same treatment (see Home.tsx, Trace.tsx). `tone` covers the semantic
 * panels (a verified match, an infeasible search) that used to be
 * hand-duplicated per component.
 */
export function Card({
  as: Component = "div",
  padding = "md",
  tone = "default",
  interactive = false,
  className = "",
  ...props
}: CardProps) {
  return (
    <Component
      className={`border rounded-lg shadow-sm shadow-black/20 ${TONE[tone]} ${PADDING[padding]} ${
        interactive ? "transition-colors hover:border-ledger-500" : ""
      } ${className}`}
      {...props}
    />
  );
}
